import { Box, IconButton, SvgIcon, Tooltip } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import IntelligentHealth from '../../assets/intelligentHealth.svg?url'
import SidebarSvg from '../../assets/map-icons/sidebar.svg?react'
import useLayout from '../../hooks/useLayout'
import { hoverScrollbarSx } from '../scroll-area'
import type { NavSectionProps } from './NavSection'
import NavSection from './NavSection'

interface Props {
  id: string
  sections: Array<NavSectionProps>
}

const SidebarToggle = () => {
  const { t } = useTranslation()
  const { isSidebarOpen, setIsSidebarOpen } = useLayout()
  const label = t(isSidebarOpen ? 'common.closeSidebar' : 'common.openSidebar')

  const handleToggle = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  return (
    <Tooltip title={label} placement='right'>
      <IconButton
        onClick={handleToggle}
        aria-label={label}
        sx={{
          display: { xs: 'none', lg: 'inline-flex' },
          width: 28,
          height: 28,
          borderRadius: '6px',
          color: 'text.secondary',
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.text.primary, 0.06),
            color: 'text.primary',
          },
        }}
      >
        <SvgIcon
          component={SidebarSvg}
          inheritViewBox
          sx={{
            fontSize: 18,
            fill: 'none',
            '& rect, & path, & line': {
              fill: 'none',
              stroke: 'currentColor',
            },
          }}
        />
      </IconButton>
    </Tooltip>
  )
}

const SidebarFooter = () => {
  const { isSidebarOpen } = useLayout()

  return (
    <Box
      sx={{
        display: isSidebarOpen ? 'flex' : { xs: 'none', lg: 'flex' },
        alignItems: 'center',
        justifyContent: isSidebarOpen ? 'space-between' : 'center',
        flexShrink: 0,
        pl: isSidebarOpen ? 3 : 0, // 24px to align with the sidebar icon column
        pr: isSidebarOpen ? 1.5 : 0,
        py: 1.5,
      }}
    >
      {isSidebarOpen && (
        <img
          src={IntelligentHealth}
          alt={'Powered by Intelligent Health'}
          style={{ height: 'auto', width: '140px' }}
        />
      )}
      <SidebarToggle />
    </Box>
  )
}

export const SidebarContent = ({ sections, id }: Props) => {
  const location = useLocation()

  return (
    <Box
      id={id}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: '1 1 auto',
          overflowX: 'hidden', // Never show horizontal scrollbar
          overflowY: 'auto', // Show vertical scrollbar only when needed
          ...hoverScrollbarSx,
        }}
      >
        <Box sx={{ px: 1, pt: 1.5, pb: 2 }}>
          {sections.map((section, index) => (
            <NavSection
              key={`${section.pathname}${index}`}
              pathname={location.pathname}
              sx={{
                '& + &': {
                  mt: '2px',
                },
              }}
              {...section}
            />
          ))}
        </Box>
      </Box>
      <SidebarFooter />
    </Box>
  )
}
